import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Segmented,
  Switch,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  DesktopOutlined,
  DollarOutlined,
  GiftOutlined,
  GlobalOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SettingOutlined,
  SoundOutlined,
  SunOutlined,
  TeamOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useColorMode, type ColorModePref } from '@/app/colorMode';
import { changeLang } from '@/app/i18n';
import { useAuth } from '@/shared/auth/AuthProvider';
import { hasAccess, isAdmin, subjectOf, type RouteAccess } from '@/shared/auth/access';
import { PATHS, type AppPath } from '@/routes/paths';
import { useEventInvalidations } from '@/features/events/useEventInvalidations';
import { SseToasts } from '@/features/events/SseToasts';
import { NotificationCenter } from '@/features/notifications/NotificationCenter';
import { BroadcastDrawer } from '@/features/notifications/BroadcastDrawer';
import { useStreamHealth, type StreamStatus } from '@/shared/ui/streamHealth';
import { NoteText } from '@/shared/ui/primitives';
const { Header, Content, Sider } = Layout;
const SIDER_WIDTH = 200;
const SIDER_COLLAPSED_WIDTH = 64;
export const ROUTE_ACCESS = {
  [PATHS.steamOrders]: 'any-logged-in',
  [PATHS.redeemWorkbench]: 'any-logged-in',
  [PATHS.redeems]: 'any-logged-in',
  [PATHS.redeemFailed]: 'any-logged-in',
  [PATHS.proxies]: 'any-logged-in',
  [PATHS.cdkeys]: 'admin-or-operator',
  [PATHS.fxQuotes]: 'granted:fxQuotes',
  [PATHS.users]: 'granted:userManagement',
  [PATHS.settings]: 'any-logged-in',
} as const satisfies Partial<Record<AppPath, RouteAccess>>;
export type RoutePath = keyof typeof ROUTE_ACCESS;
type NavLabelKey =
  | 'nav.steamOrders'
  | 'nav.redeems'
  | 'nav.proxies'
  | 'nav.cdkeys'
  | 'nav.fxQuotes'
  | 'nav.users';
type NavGroupKey = 'nav.groupOverview' | 'nav.groupSteam';
interface NavLeaf {
  path: RoutePath;
  labelKey: NavLabelKey;
  icon: ReactNode;
}
interface NavGroup {
  key: string;
  labelKey: NavGroupKey;
  leaves: readonly NavLeaf[];
}
const NAV_GROUPS: readonly NavGroup[] = [
  {
    key: 'group-overview',
    labelKey: 'nav.groupOverview',
    leaves: [
      { path: PATHS.users, labelKey: 'nav.users', icon: <TeamOutlined /> },
      { path: PATHS.proxies, labelKey: 'nav.proxies', icon: <GlobalOutlined /> },
    ],
  },
  {
    key: 'group-steam',
    labelKey: 'nav.groupSteam',
    leaves: [
      { path: PATHS.steamOrders, labelKey: 'nav.steamOrders', icon: <IdcardOutlined /> },
      { path: PATHS.cdkeys, labelKey: 'nav.cdkeys', icon: <GiftOutlined /> },
      { path: PATHS.redeems, labelKey: 'nav.redeems', icon: <WalletOutlined /> },
      { path: PATHS.fxQuotes, labelKey: 'nav.fxQuotes', icon: <DollarOutlined /> },
    ],
  },
];
export { NAV_GROUPS };
export function LangSwitch() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en') ?? false;
  return (
    <Tooltip title={t('app.language')}>
      <Flex align="center" gap={4}>
        <GlobalOutlined />
        <Switch
          size="small"
          checked={isEnglish}
          checkedChildren="EN"
          unCheckedChildren="中"
          onChange={(checked) => void changeLang(checked ? 'en-US' : 'zh-CN')}
          aria-label={t('app.language')}
        />
      </Flex>
    </Tooltip>
  );
}
export function ThemeSwitch() {
  const { t } = useTranslation();
  const { preference, setPreference } = useColorMode();
  return (
    <Segmented<ColorModePref>
      size="small"
      value={preference}
      onChange={setPreference}
      aria-label={t('app.theme')}
      options={[
        { value: 'light', icon: <SunOutlined />, tooltip: t('app.light') },
        { value: 'dark', icon: <MoonOutlined />, tooltip: t('app.dark') },
        { value: 'system', icon: <DesktopOutlined />, tooltip: t('app.system') },
      ]}
    />
  );
}
const STREAM_STATUS_PRESENT: Record<
  StreamStatus,
  { key: string; color: 'success' | 'processing' | 'warning' | 'default'; hint: string }
> = {
  idle: { key: 'notifications.stream.idle', color: 'default', hint: '' },
  open: { key: 'notifications.stream.open', color: 'success', hint: '' },
  connecting: {
    key: 'notifications.stream.connecting',
    color: 'processing',
    hint: 'notifications.stream.degradedHint',
  },
  retrying: {
    key: 'notifications.stream.retrying',
    color: 'warning',
    hint: 'notifications.stream.lostHint',
  },
  offline: {
    key: 'notifications.stream.offline',
    color: 'default',
    hint: 'notifications.stream.degradedHint',
  },
};
function StreamIndicator() {
  const { t } = useTranslation();
  const { status, lastError, everConnected } = useStreamHealth();
  const present = STREAM_STATUS_PRESENT[status];
  if (!everConnected && status === 'idle') return null;
  const hint = present.hint === '' ? '' : t(present.hint);
  const title = (
    <Flex vertical gap={4}>
      <span>{t(present.key)}</span>
      {hint === '' ? null : <span>{hint}</span>}
      {lastError === null ? null : (
        <Flex align="center" gap={4}>
          <NoteText copyable={{ text: lastError }}>
            {t('notifications.stream.detailLabel')}: {lastError}
          </NoteText>
        </Flex>
      )}
    </Flex>
  );
  return (
    <Tooltip title={title}>
      <Badge
        status={present.color}
        text={t(present.key)}
        aria-label={t('notifications.stream.statusLabel')}
        data-testid="stream-indicator"
      />
    </Tooltip>
  );
}
export function AppLayout() {
  const { t } = useTranslation();
  const { token } = antdTheme.useToken();
  const { resolved } = useColorMode();
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const belowLgRef = useRef(false);
  // 服务端说「这个会话已经没了」（撤销 / 禁用 / 软删）⇒ 清场回登录页。
  //
  // ⚠️ **为什么必须有这条**：`EventSource` 看不到 HTTP 状态码 ⇒ 客户端分不出
  // 「会话没了」与「网络抖了」⇒ 只能靠服务端主动发控制帧说一声
  // （**为什么**见 `features/events/controlFrames.ts` 的模块导语）。
  //
  // ⚠️ 走 `logout()` 而不是直接清：它是 AuthProvider 唯一的清场入口
  // （登出 cookie + 清 token + 清档案）。
  const handleSessionEnded = useCallback((): void => {
    void logout().then(() => navigate(PATHS.login, { replace: true }));
  }, [logout, navigate]);
  useEventInvalidations({ onSessionEnded: handleSessionEnded });
  const items = useMemo<NonNullable<MenuProps['items']>>(
    () =>
      NAV_GROUPS.map((group) => ({
        key: group.key,
        type: 'group' as const,
        label: t(group.labelKey),
        children: group.leaves
          .filter((leaf) => hasAccess(subjectOf(me), ROUTE_ACCESS[leaf.path]))
          .map((leaf) => ({ key: leaf.path, icon: leaf.icon, label: t(leaf.labelKey) })),
      })),
    [me, t],
  );
  const selectedKeys = useMemo(() => {
    const hit = NAV_GROUPS.flatMap((group) => group.leaves).find(
      (leaf) =>
        hasAccess(subjectOf(me), ROUTE_ACCESS[leaf.path]) &&
        (location.pathname === leaf.path || location.pathname.startsWith(`${leaf.path}/`)),
    );
    return hit === undefined ? [] : [hit.path];
  }, [me, location.pathname]);
  const userMenuItems = useMemo<NonNullable<MenuProps['items']>>(
    () => [
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('nav.settings'),
        onClick: () => navigate(PATHS.settings),
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('nav.logout'),
        danger: true,
        onClick: () => {
          void logout().then(() => navigate(PATHS.login, { replace: true }));
        },
      },
    ],
    [t, logout, navigate],
  );
  if (me === null) return null;
  return (
    <Layout style={{ height: '100vh' }}>
      {/* ⚠️ **顶栏通栏**：Header 是外层 Layout 的第一个子元素，横贯全宽； Sider 放在**内层** Layout 里、顶栏之下。 Sider 若放在外层、侧栏通高，顶栏就会从 1408px 缩到 1168px， 品牌区和「未连接」状态被挤到侧栏里，整条顶栏不再是横贯的一条带。 */}
      <Header>
        <Flex align="center" justify="space-between" style={{ height: '100%' }}>
          <Flex align="center" gap="small">
            <Tooltip title={collapsed ? t('common.expand') : t('common.collapse')}>
              <Button
                type="text"
                size="small"
                aria-label={collapsed ? t('common.expand') : t('common.collapse')}
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((prev) => !prev)}
              />
            </Tooltip>
            <Flex align="center" gap="small">
              <Avatar shape="square" size="small" style={{ background: token.colorPrimary }}>
                S
              </Avatar>
              <Typography.Text strong>{t('app.title')}</Typography.Text>
            </Flex>
            <StreamIndicator />
          </Flex>
          <Flex align="center" gap="small">
            {/* 铃铛与公告入口排在「语言」**竖线的左边**：通知与公告是**同一件事的
                两面**（看通知 / 发公告），语言与主题是**环境设置** ⇒ 用竖线分组。 */}
            <NotificationCenter />
            {isAdmin(me?.role) && (
              <Tooltip title={t('notifications.broadcast.button')}>
                <Button
                  type="text"
                  size="small"
                  icon={<SoundOutlined />}
                  onClick={() => setBroadcastOpen(true)}
                  aria-label={t('notifications.broadcast.button')}
                />
              </Tooltip>
            )}
            <Divider vertical />
            <Flex align="center" gap={4}>
              <LangSwitch />
              <ThemeSwitch />
            </Flex>
            <Divider vertical />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              {}
              <Button type="text" size="small">
                <Flex align="center" gap={4}>
                  <Avatar size="small" style={{ background: token.colorPrimary }}>
                    {me.username.slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Typography.Text>{me.username}</Typography.Text>
                </Flex>
              </Button>
            </Dropdown>
          </Flex>
        </Flex>
      </Header>
      <Layout>
        <Sider
          theme={resolved}
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          collapsible
          collapsed={collapsed}
          trigger={null}
          breakpoint="lg"
          onBreakpoint={(broken) => {
            if (broken === belowLgRef.current) return;
            belowLgRef.current = broken;
            setCollapsed(broken);
          }}
          style={{ borderInlineEnd: `1px solid ${token.colorBorderSecondary}` }}
        >
          {/* 品牌区在**顶栏**，侧栏只留菜单。 */}
          <Menu
            theme={resolved}
            mode="inline"
            items={items}
            selectedKeys={selectedKeys}
            onClick={({ key }) => navigate(key)}
            style={{ borderInlineEnd: 0 }}
          />
        </Sider>
        {/* overflow:auto = 内容区自己滚。放这里而不是让整页滚：顶栏必须常驻，
            订单列表滚动时导航不该跟着跑。 */}
        <Content style={{ overflow: 'auto' }}>
          <Flex
            vertical
            gap="middle"
            style={{
              width: '100%',
              padding: 24,
            }}
          >
            <Outlet />
          </Flex>
        </Content>
        {/* 浮层提示挂在壳里而不是某个页面：它要活过**页面切换** ——
            用户提交完操作就跳走了，提示还得留着。 */}
        <SseToasts />
        {/* 公告抽屉挂在壳里而不是通知中心内部：它是 admin 的独立动作，
            与「看通知」不是同一件事，混在一起会让抽屉的关闭语义变模糊。 */}
        <BroadcastDrawer open={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
      </Layout>
    </Layout>
  );
}
